import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CamSpec } from '../../search-cam-spec/model/camspec.model';
import { PaginatedCamSpecResponse } from '../../search-cam-spec/model/paginated-camspec.model';
import { MRT } from '../models/MRT';
import { SpecLine } from '../models/SpecLine';
import { CreateSpecificationPayload } from '../models/request/CreateSpecificationPayload';
import { UpdateSpecificationLine } from '../models/request/UpdateSpecificationLine';
import { PaginatedCustomerInfoResponse } from '../../customer/models/response.model';
import { SpecificationLineResponse, SpecificationResponse } from '../models/response/response.model';

export interface UpdateSpecDescRequest {
  description: string;
  modifiedBy: string;
}
@Injectable({
  providedIn: 'root'
})
export class AssetManagementService {
  specLines: SpecLine[];
  public http = inject(HttpClient);

  private currentApplication = new BehaviorSubject<string>('');
  currentApplication$ = Observable<string>;

  private specificationCreated = new Subject<boolean>();
  specificationCreated$ = this.specificationCreated.asObservable();

  constructor() {}

  getSpecificationLines(customerId: string): Observable<SpecificationLineResponse> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.specifications}/customer/${customerId}`;

    return this.http.get<SpecificationLineResponse>(url);
  }

  getSpecificationLinesFromCustomerId(customerId: string): Observable<SpecificationLineResponse> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.specifications}/customerId/${customerId}`;

    return this.http.get<SpecificationLineResponse>(url);
  }

  getSpecification(specificationId: string): Observable<SpecificationResponse> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.specifications}/${specificationId}`;
    return this.http.get<SpecificationResponse>(url);
  }

  getMRTSpecifications(): Observable<MRT[]> {
    const url = environment.camServiceUrl + environment.serviceUris.mrtSpecification;
    return this.http.get<MRT[]>(url);
  }

  setSpecLines(specLines: SpecLine[]) {
    this.specLines = specLines;
  }

  getSpecLine(specificationId: string) {
    return this.specLines.find((spec) => spec.id === specificationId);
  }

  setCurrentApplication(data: string) {
    this.currentApplication.next(data);
  }

  getCurrentApplication() {
    return this.currentApplication.asObservable();
  }

  updateSpecificationLine(
    updateSpecificationLine: UpdateSpecificationLine,
    v4SpecificationId: string,
    username?: string,
    updateEnabled?: boolean
  ): Observable<SpecificationResponse> {
    // Remove with @EDIT-OFF
    if (!updateEnabled && (environment.envName === 'na-prod' || environment.envName === 'eu-prod' || environment.envName === 'sa-prod')) {
      return new Observable<SpecificationResponse>((x) => x.next(null));
    }
    const url = `${environment.camServiceUrl}${environment.serviceUris.specifications}/update`;
    const params = v4SpecificationId ? { v4SpecificationId } : {};
    return this.http.put<SpecificationResponse>(url, updateSpecificationLine, { params, headers: { user: username } });
  }

  addNewSpecificationLine(
    updateSpecificationLine: UpdateSpecificationLine,
    v4SpecificationId: string,
    username: string
  ): Observable<SpecificationResponse> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.specifications}/addline`;
    const params = { v4SpecificationId };
    return this.http.put<SpecificationResponse>(url, updateSpecificationLine, { params, headers: { user: username } });
  }

  searchCamSpec(searchString: string, page: number, limit: number): Observable<PaginatedCamSpecResponse> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.searchSpecification}`;
    return this.http.get<PaginatedCamSpecResponse>(url, { params: { searchString: searchString, page: page, pageSize: limit } });
  }

  searchCustomer(searchString: string, specId: string, page: number, limit: number): Observable<PaginatedCustomerInfoResponse> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.searchCustomer}`;
    return this.http.get<PaginatedCustomerInfoResponse>(url, {
      params: { searchString: searchString, specId: specId, page: page, pageSize: limit }
    });
  }

  assignCustomer(specId: string, customerIds: string[]): Observable<boolean> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.assignCustomer}`;
    return this.http.post<boolean>(url, customerIds, {
      params: { specId: specId }
    });
  }

  duplicateCamSpec(specName: string, specDescription: string, camSpec: CamSpec, user: string): Observable<boolean> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.duplicateCamSpec}`;
    return this.http.post<boolean>(url, camSpec, { params: { specName: specName, specDescription: specDescription, user } });
  }

  searchAssignedCustomers(specId: string, page: number, limit: number): Observable<PaginatedCustomerInfoResponse> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.searchAssigned}`;
    return this.http.get<PaginatedCustomerInfoResponse>(url, {
      params: { specId: specId, page: page, pageSize: limit }
    });
  }

  searchNafaAssignedCustomers(specId: string, page: number, limit: number): Observable<PaginatedCustomerInfoResponse> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.searchNafaAssigned}`;
    return this.http.get<PaginatedCustomerInfoResponse>(url, {
      params: { specId, page, pageSize: limit }
    });
  }

  deleteInactiveCamSpec(specificationId: string): Observable<void> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.specifications}/${specificationId}`;
    return this.http.put<void>(url, {});
  }

  createSpecification(specificationPayload: CreateSpecificationPayload): Observable<SpecificationResponse> {
    const url = environment.camServiceUrl + environment.serviceUris.specifications;
    return this.http.post<SpecificationResponse>(url, specificationPayload);
  }

  deleteSpecLine(specificationId: string, specLineId: string, username: string): Observable<SpecificationResponse> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.specifications}/${specificationId}`;
    return this.http.delete<SpecificationResponse>(url, { headers: { user: username }, params: { specLineId: specLineId } });
  }

  notifySpecificationCreated(success: boolean): void {
    this.specificationCreated.next(success);
  }

  updateSpecDesc(specificationId: string, payload: UpdateSpecDescRequest): Observable<void> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.specifications}/${specificationId}/header`;
    return this.http.put<void>(url, payload);
  }

  checkIfCamAlreadyExists(specName: string): Observable<boolean> {
    const url = `${environment.camServiceUrl}${environment.serviceUris.camNameCheck}?specName=${specName}`;
    return this.http.get<boolean>(url);
  }
}
